/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { UserAPI } from "../../api/User";
import { SettingsHelpVideosAtom } from "../../atoms/SettingsHelpVideos";
import { UserAtom, UserCustomCopyTemplates } from "../../atoms/UserAtom";
import { HelpVideoNameMap, IHelpContexts } from "../../types/HelpVideosTypes";
import { ICustomCopyTemplates } from "../../types/UserTypes";
import CreateCustomCopyModal from "../Modal/container/CreateCustomCopyModal";
import Warning from "../Modal/container/Warning";
import { useModal } from "../Modal/Modal";
import MergeFields from "../NewEmail/MergeFields";
import StyledButton from "../StyledButton";
import StyledTextArea from "../StyledTextArea";

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  height: fit-content;

  margin: 40px 0px;
  padding: 48px;

  border-radius: 4px;
  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  cursor: default;

  @media screen and (max-width: 1140px) {
    padding: 48px 24px;
  }
`;

const MainTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 18px;
`;

const SecondaryTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 16px;

  display: flex;
`;

const MainText = styled.div`
  color: #707070;
  font-weight: 400;
  font-size: 15px;
`;

const TextWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  grid-gap: 8px;

  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 24px;
  margin-bottom: 24px;

  border-top: 1px solid #eaeaeabf;
  padding-top: 24px;
  margin-top: 24px;

  &:first-of-type {
    border-top: unset;
    padding-top: unset;
    margin-top: unset;
  }
`;

const FormContent = styled.div`
  width: 100%;
  height: fit-content;

  padding: 0 14px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  .divider {
    width: 100%;
    height: 1px;
    background-color: #eaeaeabf;

    margin: 24px 0;
  }
`;

const SpaceBetween = styled.div`
  width: 100%;
  min-height: 42px;

  margin-bottom: 28px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  .select__value-container.select__value-container--has-value {
    svg {
      display: none;
    }
  }

  .select__option.select__option--is-focused {
    svg {
      color: #fb972e;
    }
  }

  .select__option.select__option--is-selected {
    svg {
      color: #fff;
    }
  }

  .basic-single {
    width: 100%;
  }

  @media screen and (max-width: 1140px) {
    grid-gap: 32px;
  }

  .flex-column-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    grid-gap: 8px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
`;

const TextAreaButtonsContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
`;

const FormatOptionLabelContainer = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;

  .label {
    display: flex;
    align-items: center;
    justify-content: center;
    grid-gap: 8px;

    svg {
      width: 18px;
      height: 18px;

      color: #c1c1c1;
    }
  }

  .button {
    width: 24px;
    height: 24px;

    padding: 4px;
    border-radius: 4px;

    background: unset;

    border: none;
    background: none;

    display: flex;
    align-items: center;
    justify-content: center;

    cursor: pointer;

    svg {
      width: 20px;
      height: 20px;

      color: #fb972e;
    }
  }
`;

function FileIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="16"
      viewBox="0 0 12 16"
    >
      <path
        id="Icon"
        d="M11.416-11.082,9.083-13.414A2.009,2.009,0,0,0,7.669-14H2a2,2,0,0,0-2,2V0A2,2,0,0,0,2,2h8a2.006,2.006,0,0,0,2-2V-9.669A2,2,0,0,0,11.416-11.082ZM10.5,0a.5.5,0,0,1-.5.5H2A.5.5,0,0,1,1.5,0V-12a.5.5,0,0,1,.5-.5H7V-10A1,1,0,0,0,8-9h2.472V0ZM3-5.25a.75.75,0,0,0,.75.75h4.5A.748.748,0,0,0,9-5.25.748.748,0,0,0,8.25-6H3.75A.752.752,0,0,0,3-5.25ZM8.25-3H3.75A.752.752,0,0,0,3-2.25a.751.751,0,0,0,.75.75h4.5A.75.75,0,0,0,9-2.25.749.749,0,0,0,8.25-3Z"
        transform="translate(0 14)"
        fill="currentColor"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="16"
      viewBox="0 0 14 16"
    >
      <path
        id="Delete"
        d="M8.375.75h.75A.375.375,0,0,0,9.5.375v-6.75a.375.375,0,0,0-.375-.375h-.75A.375.375,0,0,0,8-6.375V.375A.375.375,0,0,0,8.375.75ZM13.5-9.75H10.925L9.862-11.522a1.663,1.663,0,0,0-1.286-.728H5.425a1.663,1.663,0,0,0-1.286.728L3.075-9.75H.5a.5.5,0,0,0-.5.5v.5a.5.5,0,0,0,.5.5H1V2.25a1.5,1.5,0,0,0,1.5,1.5h9A1.5,1.5,0,0,0,13,2.25V-8.25h.5a.5.5,0,0,0,.5-.5v-.5A.5.5,0,0,0,13.5-9.75Zm-8.13-.909a.208.208,0,0,1,.161-.091H8.469a.208.208,0,0,1,.161.091l.546.909H4.825ZM11.5,2.25h-9V-8.25h9ZM4.875.75h.75A.375.375,0,0,0,6,.375v-6.75a.375.375,0,0,0-.375-.375h-.75a.375.375,0,0,0-.375.375V.375A.375.375,0,0,0,4.875.75Z"
        transform="translate(0 12.25)"
        fill="currentColor"
      />
    </svg>
  );
}

function FormatOptionLabel(props: {
  value: string;
  label: string;
  onDelete: () => void;
}) {
  return (
    <FormatOptionLabelContainer>
      <div className="label">
        <FileIcon />
        {props.label}
      </div>
      {props.label !== "Default" && (
        <div
          className="button"
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();

            props.onDelete();
          }}
        >
          <TrashIcon />
        </div>
      )}
    </FormatOptionLabelContainer>
  );
}

export default function ContactsSettings(): JSX.Element | null {
  const [user, setUser] = useRecoilState(UserAtom);

  const [userCustomCopyTemplates, setUserCustomCopyTemplates] = useRecoilState(
    UserCustomCopyTemplates
  );

  const modal = useModal();

  const [templateCopy, setTemplateCopy] = useState(
    user.options?.customCopy?.template || ""
  );

  const [selectedTemplate, setSelectedTemplate] = useState("Default");

  const [isLoading, setIsLoading] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const [, setHelpVideo] = useRecoilState(SettingsHelpVideosAtom);

  function handleDeleteTemplate(item: ICustomCopyTemplates) {
    modal.pushModal(
      <Warning
        title="Delete template"
        buttonsName={["Cancel", "Confirm"]}
        description={`Are you sure you want to delete the "${item.title}" template?`}
        icon={"../icons/delete.svg"}
        onOkClicked={async () => {
          const templateCopy = userCustomCopyTemplates.find(
            (tc) => tc.title === item.title
          );

          if (!templateCopy) return;

          try {
            await UserAPI.deleteCustomCopyTemplate({
              _id: templateCopy._id!,
            });

            // Remove from the array and select the default one
            setUserCustomCopyTemplates((tca) =>
              tca.filter((t) => t._id !== templateCopy._id)
            );
            setSelectedTemplate("Default");

            toast("Template successfully deleted", {
              type: "success",
            });
          } catch (error) {
            toast("Something went wrong during template deletion", {
              type: "error",
            });
          } finally {
            modal.popModal();
          }
        }}
        onCancelClicked={modal.popAll}
      />
    );
  }

  const templateCopyArrayOptions = useMemo(
    () =>
      userCustomCopyTemplates.map((item) => {
        return {
          value: item.title,
          label: item.title,
          onDelete: () => handleDeleteTemplate(item),
        };
      }),
    [userCustomCopyTemplates]
  );

  const selectedTemplateObj = useMemo(
    () =>
      userCustomCopyTemplates.find((item) => item.title === selectedTemplate),
    [selectedTemplate]
  );

  useEffect(() => {
    if (!selectedTemplate) {
      setTemplateCopy("");
      return;
    }

    setTemplateCopy(selectedTemplateObj?.template);
  }, [selectedTemplateObj]);

  useEffect(() => {
    setHelpVideo({
      helpTitle: HelpVideoNameMap[IHelpContexts.SettingsContacts],
      helpContext: IHelpContexts.SettingsContacts,
    });
  }, []);

  function handleFieldSelected(field: string) {
    const start = textAreaRef.current.selectionStart;
    const end = textAreaRef.current.selectionEnd;
    const text = textAreaRef.current.value;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);

    setTemplateCopy(`${before} ${field} ${after} `);

    textAreaRef.current.selectionStart = textAreaRef.current.selectionEnd =
      start + field.length;
    textAreaRef.current.focus();
  }

  async function handleSaveTemplate() {
    setIsLoading(true);

    const customCopy = userCustomCopyTemplates.find(
      (t) => t.title === selectedTemplate
    );

    if (!customCopy) return setIsLoading(false);

    try {
      await UserAPI.updateCustomCopyTemplate({
        _id: customCopy._id,
        title: customCopy.title,
        isDefault: customCopy.isDefault,

        template: templateCopy,
      });

      setUserCustomCopyTemplates((prevArray) =>
        prevArray.map((prevObj) => {
          if (prevObj.title === selectedTemplate) {
            return {
              ...prevObj,
              template: templateCopy,
            };
          } else {
            return prevObj;
          }
        })
      );

      toast(`Custom Copy template ${customCopy.title} saved successfully`, {
        type: "success",
      });
    } catch (err) {
      toast("There was an error saving your custom copy template", {
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }

  // async function update() {
  //   let fieldRegex = /\{\{contact.\w+\}\}/gi;
  //   setIsLoading(true);
  //   const fieldMatches = templateCopy.matchAll(fieldRegex);
  //   let next: IteratorResult<RegExpMatchArray, any>;
  //   let fields: string[] = [];

  //   while (!(next = fieldMatches.next()).done) {
  //     const currentMatch = next.value;
  //     const value = currentMatch[0] as string;

  //     fields.push(value.split(".")[1].split("}")[0].toLowerCase());
  //   }

  //   const customCopy: IVirtualField = {
  //     fields,
  //     key: "customcopy",
  //     template: templateCopy,
  //     bypass: true,
  //   };

  //   const newUser = await updateUser({
  //     ...user,
  //     options: { ...user.options, customCopy },
  //   });

  //   if (newUser) {
  //     setUser({ ...user, options: { ...user.options, customCopy } });
  //   }
  //   setIsLoading(false);
  //   toast("Success", { type: "success" });
  // }

  if (!user) return null;

  return (
    <Container>
      <TextWrapper>
        <MainTitle>Contacts</MainTitle>
        <MainText>View and manage your contacts settings.</MainText>
      </TextWrapper>

      <FormContent>
        <SpaceBetween>
          <div className="flex-column-wrapper">
            <SecondaryTitle>Custom Copy</SecondaryTitle>
            <MainText>
              Select which fields will be included in custom copy.
            </MainText>
          </div>
        </SpaceBetween>

        <SpaceBetween>
          <Select
            theme={(theme) => ({
              ...theme,
              borderRadius: 8,
              colors: {
                ...theme.colors,
                primary50: "#fdedd4",
                primary25: "#fdedd4",
                primary: "#fb972e",
              },
            })}
            formatOptionLabel={FormatOptionLabel}
            menuPlacement="auto"
            isClearable
            isSearchable
            placeholder="Select template"
            className="basic-single"
            classNamePrefix="select"
            onChange={(newValue) => {
              if (newValue?.value) {
                setSelectedTemplate(newValue.value);
                return;
              } else {
                setSelectedTemplate("");
              }
            }}
            options={templateCopyArrayOptions}
            value={templateCopyArrayOptions.find(
              (t) => t.value === selectedTemplate
            )}
          />

          <StyledButton
            inverted
            onClick={() =>
              modal.pushModal(
                <CreateCustomCopyModal
                  setSelectedTemplate={setSelectedTemplate}
                />,
                "New Custom Copy Template"
              )
            }
            css={css`
              min-width: 128px;
              margin-left: 20px;
            `}
          >
            Create New
          </StyledButton>
        </SpaceBetween>

        <TextAreaButtonsContainer>
          <StyledTextArea
            id={"text-area-template"}
            value={templateCopy}
            onChange={(e) => setTemplateCopy(e.target.value)}
            placeholder="Enter your template for your custom copy"
            css={css`
              height: 200px;
              width: 100%;
              padding: 10px 14px;
            `}
            ref={textAreaRef}
          />
          <ButtonWrapper>
            <MergeFields
              isSetting
              selectedVariable={(value) => handleFieldSelected(value)}
              removeVirtualFields
            >
              <div>
                <StyledButton
                  inverted
                  css={css`
                    min-width: 129px;
                    max-width: 129px;
                    :hover {
                      opacity: 1 !important;
                    }
                  `}
                >
                  Select Fields
                </StyledButton>
              </div>
            </MergeFields>

            <div style={{ display: "flex", gridGap: "20px" }}>
              {selectedTemplate && (
                <Fragment>
                  <StyledButton
                    inverted
                    onClick={() => setTemplateCopy("")}
                    css={css`
                      width: 80px;
                    `}
                  >
                    Clear
                  </StyledButton>
                  <StyledButton
                    // disable={!templateCopy}
                    isLoading={isLoading}
                    onClick={handleSaveTemplate}
                  >
                    Save to {selectedTemplate}
                  </StyledButton>
                </Fragment>
              )}
            </div>
          </ButtonWrapper>
        </TextAreaButtonsContainer>
      </FormContent>
    </Container>
  );
}
